/**
 * FeedbackModal Component
 * 
 * A two-step feedback collection system:
 * Step 1: Collect feedback text (required)
 * Step 2: Collect email address (optional)
 */

import { getFeedbackApiEndpoint } from '../config/api-config.js'

export class FeedbackModal {
  constructor(options = {}) {
    this.options = {
      apiEndpoint: getFeedbackApiEndpoint(),
      onFeedbackSubmitted: options.onFeedbackSubmitted || (() => { }),
      onEmailSubmitted: options.onEmailSubmitted || (() => { }),
      onClose: options.onClose || (() => { }),
      ...options
    }

    this.modal = null
    this.isVisible = false
    this.currentStep = 1
    this.feedbackData = {
      feedback: '',
      email: '',
      timestamp: null,
      userAgent: navigator.userAgent,
      url: window.location.href
    }

    this.createModal()
    this.setupEventListeners()
  }

  createModal() {
    this.modal = document.createElement('div')
    this.modal.className = 'feedback-modal-overlay hidden'
    this.modal.innerHTML = this.getModalHTML()
    document.body.appendChild(this.modal)
  }

  getModalHTML() {
    return `
            <div class="feedback-modal">
                <div class="feedback-modal-content">
                    <!-- Step 1: Feedback Collection -->
                    <div class="feedback-step feedback-step-1 active">
                        <div class="feedback-header">
                            <h3>💡 Help us improve!</h3>
                            <button class="feedback-close" aria-label="Close feedback modal">×</button>
                        </div>
                        <div class="feedback-body">
                            <p>Your feedback helps make this tool better for everyone. What would you like to see improved?</p>
                            <textarea 
                                class="feedback-textarea" 
                                placeholder="Share your thoughts, suggestions, or report issues...&#10;&#10;For example:&#10;• 'The tree view could be faster with large files'&#10;• 'Add a dark mode option'&#10;• 'Export to CSV would be helpful'"
                                rows="6"
                                maxlength="1000"
                            ></textarea>
                            <div class="feedback-char-count">
                                <span class="char-current">0</span>/<span class="char-max">1000</span> characters
                            </div>
                        </div>
                        <div class="feedback-footer">
                            <button class="btn btn-secondary feedback-cancel">Maybe later</button>
                            <button class="btn btn-primary feedback-submit" disabled>
                                Share feedback
                                <span class="btn-icon">→</span>
                            </button>
                        </div>
                    </div>

                    <!-- Step 2: Email Collection -->
                    <div class="feedback-step feedback-step-2">
                        <div class="feedback-header">
                            <h3>🙏 Thank you!</h3>
                            <button class="feedback-close" aria-label="Close feedback modal">×</button>
                        </div>
                        <div class="feedback-body">
                            <div class="feedback-success-message">
                                <div class="feedback-checkmark">✓</div>
                                <p><strong>Your feedback has been received!</strong></p>
                                <p>Want to be notified about updates or get a response?</p>
                            </div>
                            <input 
                                type="email" 
                                class="feedback-email" 
                                placeholder="your.email@example.com (optional)"
                                maxlength="100"
                            />
                            <div class="feedback-email-benefits">
                                <small>
                                    <span class="benefit-icon">📧</span> Get notified about new features<br>
                                    <span class="benefit-icon">💬</span> Receive responses to your feedback<br>
                                    <span class="benefit-icon">🔒</span> We'll never spam or share your email
                                </small>
                            </div>
                        </div>
                        <div class="feedback-footer">
                            <button class="btn btn-secondary feedback-skip">Skip</button>
                            <button class="btn btn-primary feedback-email-submit">
                                Stay updated
                                <span class="btn-icon">✓</span>
                            </button>
                        </div>
                    </div>

                    <!-- Step 3: Final Thank You -->
                    <div class="feedback-step feedback-step-3">
                        <div class="feedback-header">
                            <h3>🎉 All set!</h3>
                            <button class="feedback-close" aria-label="Close feedback modal">×</button>
                        </div>
                        <div class="feedback-body">
                            <div class="feedback-final-message">
                                <div class="feedback-celebration">🚀</div>
                                <p><strong>Thanks for helping us improve!</strong></p>
                                <p>Your input makes a real difference in making this tool better for everyone.</p>
                            </div>
                        </div>
                        <div class="feedback-footer">
                            <button class="btn btn-primary feedback-done">
                                Continue using JSONly
                                <span class="btn-icon">→</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `
  }

  setupEventListeners() {
    // Close modal events
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal || e.target.classList.contains('feedback-close')) {
        this.hide()
      }
    })

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.hide()
      }
    })

    // Step 1: Feedback submission
    const textarea = this.modal.querySelector('.feedback-textarea')
    const charCurrent = this.modal.querySelector('.char-current')
    const submitBtn = this.modal.querySelector('.feedback-submit')
    const cancelBtn = this.modal.querySelector('.feedback-cancel')

    textarea.addEventListener('input', (e) => {
      const length = e.target.value.length
      charCurrent.textContent = length
      submitBtn.disabled = length < 10 // Minimum 10 characters
    })

    submitBtn.addEventListener('click', () => {
      this.submitFeedback(textarea.value)
    })

    cancelBtn.addEventListener('click', () => {
      this.hide()
    })

    // Step 2: Email submission
    const emailInput = this.modal.querySelector('.feedback-email')
    const emailSubmitBtn = this.modal.querySelector('.feedback-email-submit')
    const skipBtn = this.modal.querySelector('.feedback-skip')

    emailSubmitBtn.addEventListener('click', () => {
      this.submitEmail(emailInput.value)
    })

    skipBtn.addEventListener('click', () => {
      this.goToStep(3)
    })

    // Step 3: Done
    const doneBtn = this.modal.querySelector('.feedback-done')
    doneBtn.addEventListener('click', () => {
      this.hide()
    })

    // Enter key handling
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.ctrlKey && !submitBtn.disabled) {
        this.submitFeedback(textarea.value)
      }
    })

    emailInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.submitEmail(emailInput.value)
      }
    })
  }

  async submitFeedback(feedback) {
    try {
      this.feedbackData.feedback = feedback.trim()
      this.feedbackData.timestamp = new Date().toISOString()

      // Show loading state
      const submitBtn = this.modal.querySelector('.feedback-submit')
      const originalText = submitBtn.innerHTML
      submitBtn.innerHTML = '<span class="loading-spinner"></span> Sending...'
      submitBtn.disabled = true

      // Submit to API
      const response = await this.sendToAPI({
        type: 'feedback',
        data: {
          feedback: this.feedbackData.feedback,
          timestamp: this.feedbackData.timestamp,
          userAgent: this.feedbackData.userAgent,
          url: this.feedbackData.url
        }
      })

      if (response.success) {
        this.options.onFeedbackSubmitted(this.feedbackData)
        this.goToStep(2)
      } else {
        throw new Error(response.error || 'Failed to submit feedback')
      }

    } catch (error) {
      console.error('Error submitting feedback:', error)

      // Show error state
      const submitBtn = this.modal.querySelector('.feedback-submit')
      submitBtn.innerHTML = 'Try again'
      submitBtn.disabled = false

      this.showError('Failed to submit feedback. Please try again.')
    }
  }

  async submitEmail(email) {
    try {
      email = email.trim()

      // Validate email if provided
      if (email && !this.isValidEmail(email)) {
        this.showError('Please enter a valid email address.')
        return
      }

      this.feedbackData.email = email

      if (email) {
        // Show loading state
        const emailSubmitBtn = this.modal.querySelector('.feedback-email-submit')
        const originalText = emailSubmitBtn.innerHTML
        emailSubmitBtn.innerHTML = '<span class="loading-spinner"></span> Saving...'
        emailSubmitBtn.disabled = true

        // Submit email to API
        const response = await this.sendToAPI({
          type: 'email',
          data: {
            email: this.feedbackData.email,
            feedbackTimestamp: this.feedbackData.timestamp
          }
        })

        if (!response.success) {
          throw new Error(response.error || 'Failed to save email')
        }

        this.options.onEmailSubmitted(this.feedbackData)
      }

      this.goToStep(3)

    } catch (error) {
      console.error('Error submitting email:', error)

      // Show error state
      const emailSubmitBtn = this.modal.querySelector('.feedback-email-submit')
      emailSubmitBtn.innerHTML = 'Try again'
      emailSubmitBtn.disabled = false

      this.showError('Failed to save email. Please try again.')
    }
  }

  async sendToAPI(payload) {
    console.log('📝 Sending feedback to API:', this.options.apiEndpoint, payload)

    try {
      const response = await fetch(this.options.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('API Error Response:', response.status, errorText)
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      console.log('✅ API Response:', result)
      return result

    } catch (error) {
      console.error('API Error:', error)
      return { success: false, error: error.message }
    }
  }

  goToStep(step) {
    const steps = this.modal.querySelectorAll('.feedback-step')
    steps.forEach(s => s.classList.remove('active'))

    const targetStep = this.modal.querySelector(`.feedback-step-${step}`)
    if (targetStep) {
      targetStep.classList.add('active')
      this.currentStep = step
    }
  }

  show() {
    this.modal.classList.remove('hidden')
    this.isVisible = true

    // Focus on textarea
    setTimeout(() => {
      const textarea = this.modal.querySelector('.feedback-textarea')
      if (textarea) {
        textarea.focus()
      }
    }, 100)

    // Reset to step 1
    this.goToStep(1)

    // Clear previous data
    this.feedbackData = {
      feedback: '',
      email: '',
      timestamp: null,
      userAgent: navigator.userAgent,
      url: window.location.href
    }

    // Reset form
    const textarea = this.modal.querySelector('.feedback-textarea')
    const emailInput = this.modal.querySelector('.feedback-email')
    if (textarea) textarea.value = ''
    if (emailInput) emailInput.value = ''

    // Reset character count
    const charCurrent = this.modal.querySelector('.char-current')
    if (charCurrent) charCurrent.textContent = '0'

    // Reset submit button
    const submitBtn = this.modal.querySelector('.feedback-submit')
    if (submitBtn) {
      submitBtn.disabled = true
      submitBtn.innerHTML = 'Share feedback <span class="btn-icon">→</span>'
    }
  }

  hide() {
    this.modal.classList.add('hidden')
    this.isVisible = false
    this.options.onClose()
  }

  showError(message) {
    // Create a temporary error notification
    const errorDiv = document.createElement('div')
    errorDiv.className = 'feedback-error'
    errorDiv.textContent = message

    const body = this.modal.querySelector('.feedback-body')
    body.insertBefore(errorDiv, body.firstChild)

    // Remove after 5 seconds
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.parentNode.removeChild(errorDiv)
      }
    }, 5000)
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  destroy() {
    if (this.modal && this.modal.parentNode) {
      this.modal.parentNode.removeChild(this.modal)
    }
  }
}
